import { useEffect, useRef, useState } from 'react';
import { fieldTypeOf, fitsFieldType } from '../../field-types';
import type { FieldElement } from '../../format/form-definition';
import { isReference, type Bound } from '../../format/pieces';
import { AUTHOR_TEXTS } from '../../texts/author';
import { boundWords } from '../../validations';
import { useBuilder } from '../context';
import { sameJson } from '../same-json';
import { countEntry, dateEntry, inWrongOrder, type Entry, type Note } from './rangeEntry';
import { rangeWords, validationOf } from './rules';

export const END = { MIN: 'min', MAX: 'max' } as const;
export const ENDS = [END.MIN, END.MAX];

export type End = (typeof END)[keyof typeof END];

type Ends = { [E in End]?: Bound };

const WAITING = Symbol('waiting for a value');

interface Draft {
  inputs: { [E in End]?: unknown };
  over: Ends;
}

export const useRangeBounds = (field: FieldElement) => {
  const { form, edit } = useBuilder();
  const range = validationOf(field, 'range');
  const words = rangeWords(field);
  const card = fieldTypeOf(field);
  const cardInput = words.bounds !== 'count';
  const emptyInput: unknown = cardInput ? card.emptyValue : '';
  const stored: Ends = { min: range?.min, max: range?.max };

  const [draft, setDraft] = useState<Draft | null>(null);
  const leaveTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);
  const inputs = draft && sameJson(draft.over, stored) ? draft.inputs : {};

  const inputFor = (bound: Bound | undefined): unknown => {
    if (bound === undefined) return emptyInput;
    return isReference(bound) || cardInput ? bound : String(bound);
  };
  const inputOf = (end: End, from = inputs): unknown => (end in from ? from[end] : inputFor(stored[end]));

  const entryOf = (input: unknown): Entry => {
    if (input === WAITING) return { kind: 'unfinished' };
    if (isReference(input)) return { kind: 'bound', bound: input };
    if (!cardInput) return countEntry(typeof input === 'string' ? input : '');
    if (input === null || input === undefined) return { kind: 'empty' };
    if (!fitsFieldType(field, input)) return { kind: 'unfinished' };
    if (card.isEmpty(input)) return { kind: 'empty' };
    if (typeof input === 'string' && words.bounds === 'date') return dateEntry(input);
    return { kind: 'bound', bound: input as number };
  };

  const boundOf = (end: End, from: Draft['inputs']): Bound | undefined => {
    const entry = entryOf(inputOf(end, from));
    if (entry.kind === 'bound') return entry.bound;
    return entry.kind === 'empty' ? undefined : stored[end];
  };

  const removeRule = () => edit({ kind: 'removeValidation', fieldId: field.id, validationKind: 'range' });

  const change = (end: End, input: unknown) => {
    const next = { ...inputs, [end]: input };
    const bounds: Ends = { min: boundOf(END.MIN, next), max: boundOf(END.MAX, next) };
    const emptied = bounds.min === undefined && bounds.max === undefined;
    const waits = inWrongOrder(bounds.min, bounds.max) || (emptied && !isReference(inputOf(end)));
    setDraft({ inputs: next, over: waits ? stored : bounds });
    if (waits || sameJson(bounds, stored)) return;
    if (emptied) removeRule();
    else
      edit({
        kind: 'setValidation',
        fieldId: field.id,
        validation: { ...range, kind: 'range', min: bounds.min, max: bounds.max },
      });
  };

  const noteOf = (end: End): Note | undefined => {
    const entry = entryOf(inputOf(end));
    const bound = stored[end];
    const inEffect = bound === undefined ? undefined : boundWords(form, bound);
    if (entry.kind === 'unfinished') return { text: AUTHOR_TEXTS.range.unfinished(inEffect), error: false };
    const typed = entry.kind === 'bound' ? entry.bound : undefined;
    if (sameJson(typed, bound)) return undefined;
    const other = boundOf(end === END.MIN ? END.MAX : END.MIN, inputs);
    const wrongOrder = end === END.MIN ? inWrongOrder(typed, other) : inWrongOrder(other, typed);
    if (!wrongOrder) return undefined;
    const otherEnd = words[end === END.MIN ? END.MAX : END.MIN];
    return { text: AUTHOR_TEXTS.range.wrongOrder(end, otherEnd, words.bounds === 'date', inEffect), error: true };
  };

  const leaving = {
    onBlur: () => {
      window.clearTimeout(leaveTimer.current);
      leaveTimer.current = window.setTimeout(() => {
        if (
          document.hasFocus() &&
          range &&
          boundOf(END.MIN, inputs) === undefined &&
          boundOf(END.MAX, inputs) === undefined
        )
          removeRule();
      });
    },
    onFocus: () => window.clearTimeout(leaveTimer.current),
  };

  return {
    range,
    words,
    inputOf: (end: End) => {
      const input = inputOf(end);
      return input === WAITING ? emptyInput : input;
    },
    change,
    noteOf,
    typeValue: (end: End) => change(end, WAITING),
    leaving,
  };
};
